import React from "react";
import { Link } from "react-router-dom";
import { trackMarketingFunnel } from "../../utils/marketingAnalytics";

// Icons matching UI-mock (Lucide-style): plug, map-pin, chart-column, zap, navigation, trees, file-check, route
const PlugIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-emerald-400">
    <path d="M12 22v-5" /><path d="M9 8V2" /><path d="M15 8V2" /><path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
  </svg>
);
const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2 w-5 h-5">
    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
  </svg>
);
const MapPinIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-emerald-400">
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" /><circle cx="12" cy="10" r="3" />
  </svg>
);
const ChartColumnIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-blue-400">
    <path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" />
  </svg>
);
const ZapIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-amber-400">
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
  </svg>
);
const NavigationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-purple-400">
    <polygon points="3 11 22 2 13 21 11 13 3 11" />
  </svg>
);
const TreesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-teal-400">
    <path d="M10 10v.2A3 3 0 0 1 8.9 16H5a3 3 0 0 1-1-5.8V10a3 3 0 0 1 6 0Z" /><path d="M7 16v6" /><path d="M13 19v3" /><path d="M12 19h8.3a1 1 0 0 0 .7-1.7L18 14h.3a1 1 0 0 0 .7-1.7L16 9h.2a1 1 0 0 0 .8-1.7L13 3l-1.4 1.5" />
  </svg>
);
const FileCheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-rose-400">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="m9 15 2 2 4-4" />
  </svg>
);
const RouteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-cyan-400">
    <circle cx="6" cy="19" r="3" /><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" /><circle cx="18" cy="5" r="3" />
  </svg>
);
const CircleCheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-emerald-500 flex-shrink-0">
    <circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" />
  </svg>
);

const FEATURES = [
  { icon: MapPinIcon, iconBg: "bg-emerald-500/10", title: "Screen high-potential parcels", desc: "Filter locations by corridor fit, freight activity, and operational access." },
  { icon: ChartColumnIcon, iconBg: "bg-blue-500/10", title: "Estimate demand and utilization", desc: "Model expected throughput and right-size station capacity." },
  { icon: ZapIcon, iconBg: "bg-amber-500/10", title: "Assess grid and constraints", desc: "Review capacity, interconnection complexity, and deployment risk early." },
  { icon: FileCheckIcon, iconBg: "bg-rose-500/10", title: "Support permitting decisions", desc: "Use zoning and contextual data to de-risk project approvals." },
];

const STATS = [
  { value: "10x", label: "Faster Analysis", desc: "Cut weeks off site selection" },
  { value: "10+", label: "Data Layers", desc: "Comprehensive data-driven insights" },
  { value: "Higher", label: "Utilization", desc: "Maximize ROI from day one" },
];

const FAQ = [
  { q: "Can we evaluate multiple geographies?", a: "Yes. Teams can compare regions and rank candidate parcels under the same scoring logic." },
  { q: "Does this help with utility coordination?", a: "Yes. Grid context is included early so high-risk sites can be filtered before engineering spend." },
  { q: "Is this only for public charging?", a: "No. It supports public corridor, depot, and mixed-use charging development strategies." },
];

const USE_CASES = [
  {
    title: "Public Charging Networks",
    desc: "Expand your charging network strategically by identifying high-demand locations along major corridors and in urban centers.",
    img: "/img/solutions/charging-public-networks.jpeg",
  },
  {
    title: "Fleet Depot Charging",
    desc: "Find optimal locations for depot charging facilities that serve commercial fleets and logistics operations.",
    img: "/img/solutions/charging-fleet-depot.jpg",
  },
  {
    title: "Destination Charging",
    desc: "Identify strategic locations near retail, hospitality, and commercial destinations for longer dwell-time charging.",
    img: "/img/solutions/charging-destination.jpg",
  },
];

function ChargerDevelopers() {
  return (
    <div className="min-h-screen bg-white">
      <section className="relative pt-40 pb-24 overflow-hidden bg-gradient-to-br from-slate-50 via-teal-50/40 to-blue-50/30">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-200/50 via-transparent to-transparent" />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-emerald-200/50 rounded-full blur-3xl" />
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent via-white/60 to-white" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 border border-emerald-200 mb-8">
              <PlugIcon />
              <span className="text-sm text-emerald-700 font-medium">Charging Station Developers</span>
            </div> */}
            <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-6">
              Find the perfect site for your
              <span className="block pb-3 bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">charging station</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-10 max-w-xl">
              ChargerPlan<sup>TM</sup> helps you identify optimal locations for medium and heavy-duty charging stations with comprehensive spatial analysis, demand forecasting, and infrastructure evaluation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                onClick={() => trackMarketingFunnel("charging_detail_primary_cta_click")}
                className="inline-flex items-center justify-center gap-2 font-medium bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-emerald-500/25 hover:scale-105 transition-all"
              >
                Request a Demo
                <ArrowRightIcon />
              </Link>
              <Link
                to="/subscription?module=charger"
                onClick={() => trackMarketingFunnel("charging_detail_secondary_cta_click")}
                className="inline-flex items-center justify-center gap-2 font-medium border border-slate-300 text-slate-700 hover:bg-slate-100 px-8 py-6 text-lg rounded-xl transition-colors"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar — py-16 bg-slate-900/50 border-y border-slate-800, 3 cols, gradient numbers */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent mb-2">{s.value}</div>
                <div className="text-slate-900 font-semibold text-lg mb-1">{s.label}</div>
                <div className="text-slate-500 text-sm">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features — py-24 bg-white, cards bg-slate-50 hover:bg-white border-slate-100 hover:border-slate-200 */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Core capabilities</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Focused analysis for location quality, station sizing, and rollout readiness.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="group bg-slate-50 hover:bg-white border border-slate-100 hover:border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                  <div className={`w-12 h-12 rounded-xl ${f.iconBg} flex items-center justify-center mb-4`}>
                    <Icon />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="relative overflow-hidden bg-slate-50 py-24">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white via-white/70 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent via-white/60 to-white" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Built for Every Charging Network</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Whether you&apos;re building public charging infrastructure or fleet depot facilities, ChargerPlan provides the insights you need.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {USE_CASES.map((item) => (
              <div key={item.title} className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="relative h-48 overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                  <h3 className="absolute bottom-4 left-5 text-xl font-bold text-white">{item.title}</h3>
                </div>
                <div className="p-6">
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {FAQ.map((item) => (
              <div key={item.q} className="rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-900 mb-2">{item.q}</h3>
                <p className="text-sm text-slate-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — py-24 bg-slate-900, radial gradient, "Ready to Find Your Next Site?" */}
      <section className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-600/10 via-transparent to-transparent" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Find Your Next Site?</h2>
          <p className="text-slate-400 mb-10">Custom pricing plans available · Enterprise support included</p>
          <Link
            to="/contact"
            onClick={() => trackMarketingFunnel("charging_detail_final_cta_click")}
            className="inline-flex items-center justify-center gap-2 font-medium h-10 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-10 py-6 text-lg rounded-xl shadow-lg shadow-emerald-500/25"
          >
            Contact Sales
            <ArrowRightIcon />
          </Link>
        </div>
      </section>
    </div>
  );
}

export default ChargerDevelopers;
